import { Button, Tooltip } from "@alpacahq/ui/old";

import { InputField } from "~/v2/components/common";
import { CryptoSelect } from "../../presentation/CryptoSelect";
import type { TransferCardControllerReturn } from "../TransferCard.controller";

interface WithdrawProps
  extends Pick<
    TransferCardControllerReturn,
    "withdraw" | "asset" | "fee" | "assetCode"
  > {
  onAddWhitelistAddressClicked: () => void;
}

export const Withdraw = ({
  fee,
  withdraw,
  assetCode,
  onAddWhitelistAddressClicked,
}: WithdrawProps) => (
  <div className="flex flex-col gap-3">
    <CryptoSelect
      label="Address"
      placeholder="Enter or select address"
      options={withdraw.addressOptions}
      value={withdraw.selectedAddress ?? ""}
      onChange={withdraw.onAddressChange}
      errorMessage={withdraw.addressError ?? undefined}
      emptyMessage={
        <div className="flex w-full items-center justify-center p-6">
          <p className="m-0 p-0 text-center font-medium text-gray-500 text-sm">
            No whitelisted addresses found. Add one to continue.
          </p>
        </div>
      }
      bottomButton={
        <Button
          variant="v2/filled"
          size="v2/base"
          className="w-full"
          onClick={onAddWhitelistAddressClicked}
        >
          Add New Address
        </Button>
      }
      allowWrap
    />

    <div className="flex flex-col gap-1">
      <InputField
        id="withdrawal-amount"
        label="Amount"
        secondaryLabel={`Balance: ${withdraw.availableBalance}`}
        variant="chip"
        value={withdraw.amount}
        onChange={withdraw.onAmountChange}
        placeholder="Enter amount"
        error={withdraw.amountError ?? undefined}
        chips={{
          items: [{ key: "max", label: "Max" }],
          onChipClick: (key) => {
            if (key === "max" && !withdraw.isMaxDisabled) {
              withdraw.onMaxAmountClick();
            }
          },
        }}
        fullWidth
        className="[&_input::placeholder]:font-sans [&_input]:font-mono"
      />
      {!withdraw.amountError && (
        <p className="m-0 p-0 text-gray-800 text-xs">
          {withdraw.usdAmount ? `${withdraw.usdAmount}` : ""}
        </p>
      )}
    </div>

    <div className="flex flex-col gap-1">
      <p className="m-0 flex items-center justify-between gap-2 p-0 text-sm">
        <Tooltip content="This is an estimated network fee meant to cover the cost for blockchain node operators to confirm the transaction. This is not a fee paid to Alpaca nor a fee Alpaca profits from.">
          <span className="cursor-help text-gray-700 underline decoration-dotted underline-offset-4">
            Network Fee
          </span>
        </Tooltip>
        <span className="font-medium text-gray-950">
          {fee}
          {assetCode ? ` ${assetCode}` : ""}
        </span>
      </p>
      <p className="m-0 flex items-center justify-between gap-2 p-0 text-base">
        <span className="font-semibold text-gray-800">Amount to Receive</span>
        <span className="font-medium text-gray-950">
          {withdraw.amountReceived}
          {assetCode ? ` ${assetCode}` : ""}
        </span>
      </p>
    </div>

    <div className="flex items-center justify-between gap-2">
      <Button
        type="button"
        onClick={withdraw.onCancel}
        variant="v2/stroke"
        className="w-full border-gray-400"
      >
        Cancel
      </Button>
      <Button
        type="submit"
        disabled={withdraw.isSubmitDisabled}
        onClick={withdraw.onSubmit}
        variant="v2/filled"
        loading={withdraw.isSubmitLoading}
        className="w-full"
      >
        Withdraw
      </Button>
    </div>
  </div>
);
